import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eraser, Eye, Plus, Search, Trash2 } from 'lucide-react';

import { ALLOWED_ATTACHMENT_TYPES, api, ApiError } from '../api/client';
import type { ApplicantSearchResult, CurrentUser } from '../api/types';
import { AttachmentDropZone } from '../components/AttachmentDropZone';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { IssueSelect } from '../components/IssueSelect';
import { useToast } from '../components/Toast';
import { awstLocalToUtcIso, utcIsoToAwstLocal } from '../format';

/** "Now", expressed as AWST wall-clock numbers for a datetime-local input. */
const nowAwstLocal = () => utcIsoToAwstLocal(new Date().toISOString());
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface PendingFile {
  id: string;
  file: File;
  note: string;
}

function PendingAttachments({
  files,
  onAdd,
  onRemove,
  onNoteChange,
  onError,
}: {
  files: PendingFile[];
  onAdd: (file: File) => void;
  onRemove: (id: string) => void;
  onNoteChange: (id: string, note: string) => void;
  onError: (message: string) => void;
}) {
  const handleFiles = (picked: File[]) => {
    for (const file of picked) {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        onError(`${file.name}: this file type is not allowed`);
      } else if (file.size > MAX_ATTACHMENT_BYTES) {
        onError(`${file.name}: files must be ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB or smaller`);
      } else {
        onAdd(file);
      }
    }
  };

  const preview = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener,noreferrer');
    // The tab has its own reference to the blob by the time it opens; safe to
    // release ours shortly after rather than leak it for the page's lifetime.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <div>
      <AttachmentDropZone onFiles={handleFiles} hint="PDF, Word, Excel, images — up to 25 MB each" />
      {files.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {files.map((f) => (
            <div key={f.id} className="log-row">
              <span style={{ minWidth: 160 }}>{f.file.name}</span>
              <input
                value={f.note}
                placeholder="Add a note for this file (optional, max 1000 characters)"
                maxLength={1000}
                onChange={(e) => onNoteChange(f.id, e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="button" className="btn small" onClick={() => preview(f.file)} aria-label="Preview" title="Preview">
                <Eye size={14} aria-hidden />
              </button>
              <button type="button" className="btn small danger" onClick={() => onRemove(f.id)} aria-label="Remove" title="Remove">
                <Trash2 size={14} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  application_number: '',
  loan_number: '',
  first_name: '',
  last_name: '',
  complainant_email: '',
  complainant_phone: '',
  complainant_type: '9',
  complainant_gender: '',
  assigned_to: '',
  channel: 'Unknown',
  direct_or_rep: 'Directly',
  representative_name: '',
  date_received: nowAwstLocal(),
  description: '',
  desired_outcome: '',
  is_sensitive: false,
  acknowledged: true,
  issue_a: 999,
  issue_b: 999,
  issue_c: 999,
};

type FormState = typeof emptyForm;

export function NewComplaint({ user }: { user: CurrentUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show, element: toastElement } = useToast();

  const [form, setForm] = useState<FormState>({ ...emptyForm, assigned_to: user.email });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [lookupQuery, setLookupQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ApplicantSearchResult[] | null>(null);

  const { data: reference, isLoading } = useQuery({
    queryKey: ['reference'],
    queryFn: api.reference,
    staleTime: 15 * 60_000,
  });

  const { data: assignableUsers } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: api.listAssignableUsers,
    staleTime: 15 * 60_000,
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const applyResult = (result: ApplicantSearchResult) => {
    if (result.application_id) set('application_number', result.application_id);
    if (result.loan_id) set('loan_number', result.loan_id);
    if (result.first_name) set('first_name', result.first_name);
    if (result.last_name) set('last_name', result.last_name);
    if (result.email) set('complainant_email', result.email);
    if (result.phone) set('complainant_phone', result.phone);
    show(
      `Prefilled ${[result.first_name, result.last_name].filter(Boolean).join(' ')}${
        result.date_of_birth ? ` (DOB ${result.date_of_birth})` : ''
      } — check this is the right person before continuing`
    );
  };

  const lookup = useMutation({
    mutationFn: () => api.searchApplicantProfiles(lookupQuery.trim()),
    onSuccess: ({ items }) => {
      if (items.length === 0) {
        setSearchResults([]);
        set('application_number', emptyForm.application_number);
        set('loan_number', emptyForm.loan_number);
        set('first_name', emptyForm.first_name);
        set('last_name', emptyForm.last_name);
        set('complainant_email', emptyForm.complainant_email);
        set('complainant_phone', emptyForm.complainant_phone);
        show('No matching applicant found — fields reset, enter details manually below', 'error');
      } else {
        setSearchResults(items);
      }
    },
    onError: (err: unknown) => show(err instanceof Error ? err.message : 'Could not search', 'error'),
  });

  const runLookup = () => {
    setSearchResults(null);
    if (lookupQuery.trim()) lookup.mutate();
  };

  const clearForm = () => {
    if (!confirm('Clear the form? All entered details will be lost.')) return;
    setForm({ ...emptyForm, assigned_to: user.email });
    setFieldErrors({});
    setPendingFiles([]);
    setLookupQuery('');
    setSearchResults(null);
    show('Form cleared');
  };

  const mutation = useMutation({
    mutationFn: () => api.createComplaint({ ...form, date_received: awstLocalToUtcIso(form.date_received) }),
    onSuccess: async (created) => {
      setFieldErrors({});
      await queryClient.invalidateQueries({ queryKey: ['complaints'] });

      let failed = 0;
      for (const { file, note } of pendingFiles) {
        try {
          await api.uploadAttachment(created.id, file, note || undefined);
        } catch {
          failed += 1;
        }
      }

      if (failed > 0) {
        show(
          `Complaint ${created.reference} lodged. ${failed} file(s) could not be attached — try again from the complaint.`,
          'error'
        );
      } else {
        show(`Complaint ${created.reference} lodged`);
      }
      navigate(`/complaints/${created.id}`);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.details?.length) {
        setFieldErrors(Object.fromEntries(error.details.map((d) => [d.field, d.message])));
        show('Check the highlighted fields', 'error');
      } else {
        show(error instanceof Error ? error.message : 'Could not lodge the complaint', 'error');
      }
    },
  });

  if (isLoading || !reference) return <Loading message="Loading reference data…" />;

  return (
    <div className="page">
      <h1 className="page-title">New complaint</h1>
      <p className="page-subtitle">
        The reference is allocated automatically when the complaint is lodged.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div className="card">
          <h2 className="section-title">Customer lookup</h2>
          <Field
            label="Search"
            htmlFor="customer_lookup_search"
            hint="Looks up the applicant's profile by application number, applicant id, loan id, name or email"
          >
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                id="customer_lookup_search"
                placeholder="Application no., applicant id, loan id, name or email…"
                value={lookupQuery}
                style={{ flex: 1 }}
                onChange={(e) => setLookupQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  runLookup();
                }}
              />
              <button type="button" className="btn" disabled={lookup.isPending || !lookupQuery.trim()} onClick={runLookup}>
                <Search size={14} aria-hidden /> {lookup.isPending ? 'Searching…' : 'Search'}
              </button>
            </div>
          </Field>
          {searchResults && (
            <div style={{ marginTop: 14 }}>
              {searchResults.length === 0 ? (
                <p className="faint" style={{ fontSize: 12 }}>No matching applicant found.</p>
              ) : (
                <>
                  <p className="faint" style={{ fontSize: 12, marginBottom: 6 }}>
                    Select from following option ({searchResults.length} found):
                  </p>
                  {searchResults.map((result) => (
                    <label key={result.applicant_id} className="log-row" style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="applicant_result"
                        onChange={() => applyResult(result)}
                        style={{ marginRight: 4 }}
                      />
                      <span style={{ minWidth: 160 }}>
                        {[result.first_name, result.last_name].filter(Boolean).join(' ') || '(no name)'}
                      </span>
                      <span className="faint" style={{ fontSize: 11 }}>
                        {[result.application_id && `App ${result.application_id}`, result.loan_id && `Loan ${result.loan_id}`]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      <span className="faint" style={{ fontSize: 11, flex: 1 }}>{result.email}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="section-title">Application Detail</h2>
          <div className="grid">
            <Field
              label="Application number"
              htmlFor="application_number"
              error={fieldErrors.application_number}
              hint="Required if there is no loan number"
            >
              <input
                id="application_number"
                value={form.application_number}
                aria-invalid={Boolean(fieldErrors.application_number)}
                onChange={(e) => set('application_number', e.target.value)}
              />
            </Field>
            <Field label="Loan number" htmlFor="loan_number" hint="Required if there is no application number">
              <input id="loan_number" value={form.loan_number} onChange={(e) => set('loan_number', e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="card">
          <h2 className="section-title">Complainant</h2>
          <div className="grid">
            <Field label="First name" htmlFor="first_name" required error={fieldErrors.first_name}>
              <input
                id="first_name"
                value={form.first_name}
                placeholder="Jane"
                aria-invalid={Boolean(fieldErrors.first_name)}
                onChange={(e) => set('first_name', e.target.value)}
              />
            </Field>
            <Field label="Last name" htmlFor="last_name" required error={fieldErrors.last_name}>
              <input
                id="last_name"
                value={form.last_name}
                placeholder="Smith"
                aria-invalid={Boolean(fieldErrors.last_name)}
                onChange={(e) => set('last_name', e.target.value)}
              />
            </Field>
            <Field label="Email address" htmlFor="complainant_email" required error={fieldErrors.complainant_email}>
              <input
                id="complainant_email"
                type="email"
                required
                value={form.complainant_email}
                placeholder="complainant@example.com"
                aria-invalid={Boolean(fieldErrors.complainant_email)}
                onChange={(e) => set('complainant_email', e.target.value)}
              />
            </Field>
            <Field label="Phone number" htmlFor="complainant_phone">
              <input
                id="complainant_phone"
                value={form.complainant_phone}
                placeholder="04XX XXX XXX"
                onChange={(e) => set('complainant_phone', e.target.value)}
              />
            </Field>
            <Field label="Complainant type" htmlFor="complainant_type" required>
              <select
                id="complainant_type"
                value={form.complainant_type}
                onChange={(e) => set('complainant_type', e.target.value)}
              >
                {reference.complainantTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.code} — {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Gender" htmlFor="complainant_gender" required error={fieldErrors.complainant_gender}>
              <select
                id="complainant_gender"
                required
                value={form.complainant_gender}
                aria-invalid={Boolean(fieldErrors.complainant_gender)}
                onChange={(e) => set('complainant_gender', e.target.value)}
              >
                <option value="" disabled>
                  Select…
                </option>
                {reference.genders.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Lodged by" htmlFor="lodged_by" hint="Taken from your sign-in">
              <input id="lodged_by" value={user.email} disabled />
            </Field>
            <Field label="Assign to" htmlFor="assigned_to" hint="Defaults to you — change to hand this off">
              <select id="assigned_to" value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)}>
                {!assignableUsers?.items.some((u) => u.email === form.assigned_to) && (
                  <option value={form.assigned_to}>{form.assigned_to}</option>
                )}
                {assignableUsers?.items.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.display_name ? `${u.display_name} (${u.email})` : u.email}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <div className="card">
          <h2 className="section-title">Complaint details</h2>
          <div className="grid">
            <Field label="Channel" htmlFor="channel" required>
              <select id="channel" value={form.channel} onChange={(e) => set('channel', e.target.value)}>
                {reference.channels.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Date & time received"
              htmlFor="date_received"
              required
              error={fieldErrors.date_received}
              hint="AWST"
            >
              <input
                id="date_received"
                type="datetime-local"
                max={nowAwstLocal()}
                value={form.date_received}
                aria-invalid={Boolean(fieldErrors.date_received)}
                onChange={(e) => set('date_received', e.target.value)}
              />
            </Field>
            <Field label="Made directly or by a representative" htmlFor="direct_or_rep">
              <select
                id="direct_or_rep"
                value={form.direct_or_rep}
                onChange={(e) => set('direct_or_rep', e.target.value)}
              >
                <option value="Directly">Directly</option>
                <option value="Representative">Representative</option>
              </select>
            </Field>
            <Field
              label="Representative name"
              htmlFor="representative_name"
              error={fieldErrors.representative_name}
              hint="Required when lodged by a representative"
            >
              <input
                id="representative_name"
                value={form.representative_name}
                placeholder="Company or person name"
                disabled={form.direct_or_rep !== 'Representative'}
                aria-invalid={Boolean(fieldErrors.representative_name)}
                onChange={(e) => set('representative_name', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid full" style={{ marginTop: 14 }}>
            <Field label="Issue 1" htmlFor="issue_a" required>
              <IssueSelect id="issue_a" issues={reference.issues} value={form.issue_a} onChange={(v) => set('issue_a', v)} />
            </Field>
            <Field label="Issue 2" htmlFor="issue_b">
              <IssueSelect id="issue_b" issues={reference.issues} value={form.issue_b} onChange={(v) => set('issue_b', v)} />
            </Field>
            <Field label="Issue 3" htmlFor="issue_c">
              <IssueSelect id="issue_c" issues={reference.issues} value={form.issue_c} onChange={(v) => set('issue_c', v)} />
            </Field>
          </div>

          <div className="grid full" style={{ marginTop: 14 }}>
            <Field label="Complaint description" htmlFor="description" required error={fieldErrors.description}>
              <textarea
                id="description"
                value={form.description}
                placeholder="Describe the complaint in detail... (max 20,000 characters)"
                maxLength={20_000}
                aria-invalid={Boolean(fieldErrors.description)}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
            <Field label="Desired outcome" htmlFor="desired_outcome">
              <textarea
                id="desired_outcome"
                value={form.desired_outcome}
                placeholder="What resolution is the customer seeking? (max 20,000 characters)"
                maxLength={20_000}
                onChange={(e) => set('desired_outcome', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid" style={{ marginTop: 14 }}>
            <Field label="Sensitive matter" htmlFor="is_sensitive">
              <select
                id="is_sensitive"
                value={form.is_sensitive ? '1' : '0'}
                onChange={(e) => set('is_sensitive', e.target.value === '1')}
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </Field>
            <Field
              label="Receipt acknowledged to complainant"
              htmlFor="acknowledged"
              hint="RG 271 requires acknowledgement as soon as practicable"
            >
              <select
                id="acknowledged"
                value={form.acknowledged ? '1' : '0'}
                onChange={(e) => set('acknowledged', e.target.value === '1')}
              >
                <option value="0">Not yet</option>
                <option value="1">Yes</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="card">
          <h2 className="section-title">Attachments</h2>
          <PendingAttachments
            files={pendingFiles}
            onAdd={(file) => setPendingFiles((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, file, note: '' }])}
            onRemove={(id) => setPendingFiles((prev) => prev.filter((f) => f.id !== id))}
            onNoteChange={(id, note) =>
              setPendingFiles((prev) => prev.map((f) => (f.id === id ? { ...f, note } : f)))
            }
            onError={(message) => show(message, 'error')}
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn primary" disabled={mutation.isPending}>
            <Plus size={16} aria-hidden /> {mutation.isPending ? 'Lodging…' : 'Lodge complaint'}
          </button>
          <button type="button" className="btn" onClick={clearForm}>
            <Eraser size={16} aria-hidden /> Clear
          </button>
        </div>
      </form>

      {toastElement}
    </div>
  );
}
